import {AxiosRequestConfig} from 'axios';
import {mapKeys, snakeCase} from 'lodash-es';

import {spotheroGet} from 'api';
import {displayDateFormat, apiDateFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys} from 'utils/format-strings';
import {RawApiResponse} from 'api/api.types';
import {
    RawGetUpcomingEventsResponse,
    UpcomingEvent,
    GetUpcomingEventsResponse,
    RawGetUpcomingEventsRequest,
    GetUpcomingEventsRequest,
} from './events.types';

/**
 * Converts the given data object into the format expected by the frontend by renaming keys to camelCase.
 */
const formatUpcomingEventsResponse = (data: RawGetUpcomingEventsResponse) => {
    const formattedData: GetUpcomingEventsResponse = camelizeKeys(data);

    return formattedData;
};

/**
 * Converts the given data object into the format required by the API by renaming keys to snake_case
 * and formatting date properties (`fromDate` and `toDate`).
 */
const formatUpcomingEventsRequest = (
    data: GetUpcomingEventsRequest,
    useISOString: boolean
): RawGetUpcomingEventsRequest => {
    const formattedData = {
        ...data,
        fromDate:
            data.fromDate && useISOString
                ? dayjs(data.fromDate).toISOString()
                : data.fromDate
                  ? dayjs(data.fromDate, displayDateFormat).format(
                        apiDateFormat
                    )
                  : null,
        toDate:
            data.toDate && useISOString
                ? dayjs(data.toDate).toISOString()
                : data.toDate
                  ? dayjs(data.toDate, displayDateFormat).format(apiDateFormat)
                  : null,
    };

    return mapKeys(formattedData, (_, key) => snakeCase(key));
};

export interface GetUpcomingEventsParams {
    facilityId: number;
    requestData: GetUpcomingEventsRequest;
    useISOString?: boolean;
    config?: AxiosRequestConfig;
}

/**
 * Gets the upcoming events for a given facility.
 * @see '/api/v1/facilities/{facilityId}/tiered-event-rating-rules/'
 */
const getUpcomingEvents = async ({
    facilityId,
    requestData,
    useISOString,
    config = {},
}: GetUpcomingEventsParams) => {
    try {
        const formattedRequest = formatUpcomingEventsRequest(
            requestData,
            useISOString
        );

        const response = await spotheroGet<RawApiResponse<UpcomingEvent>[]>(
            `facilities/${facilityId}/upcoming-events/`,
            formattedRequest,
            {
                paramsSerializer: {
                    indexes: null,
                },
                ...config,
            }
        );

        const formattedResponse = formatUpcomingEventsResponse(response.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getUpcomingEvents;
